/**
 * F15 — Blocked-publish messaging (teacher tier benefits/restrictions).
 *
 * `POST /api/v1/offerings/{id}/publish` can fail with 409 and a flat body:
 *   { code: 'OFFERING_TYPE_NOT_ALLOWED' | 'OFFERING_LIMIT_REACHED',
 *     allowedOfferingTypes: string[], maxPublishedOfferings: number,
 *     currentPublishedOfferings: number, tier: string }
 * (`tier` is the literal string "NONE" when the teacher has no subscription.)
 *
 * OfferingServices.ts's shared `authFetch` helper only ever throws a plain
 * `Error` whose `message` is `json.message` when present, else the raw
 * response text. This 409 body has neither `message` nor an `errors` array,
 * so `err.message` ends up holding the raw JSON text verbatim — we recover
 * the structured body from that here rather than teaching the generic fetch
 * helper about one specific endpoint's error shape.
 */

export type OfferingPublishBlockCode = 'OFFERING_TYPE_NOT_ALLOWED' | 'OFFERING_LIMIT_REACHED';

export interface OfferingPublishBlockReason {
  code: OfferingPublishBlockCode;
  allowedOfferingTypes: string[];
  maxPublishedOfferings: number;
  currentPublishedOfferings: number;
  /** Literal "NONE" when the teacher has no active subscription. */
  tier: string;
}

/** Minimal shape this module needs from `TeacherEntitlementsResponse` — kept
 * structural so callers don't need to import the full response type. */
export interface EntitlementsLike {
  tier: string | null;
  allowedOfferingTypes: string[];
  maxPublishedOfferings: number;
  currentPublishedOfferings: number;
}

function isOfferingPublishBlockReason(value: unknown): value is OfferingPublishBlockReason {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    (v.code === 'OFFERING_TYPE_NOT_ALLOWED' || v.code === 'OFFERING_LIMIT_REACHED') &&
    Array.isArray(v.allowedOfferingTypes) &&
    typeof v.maxPublishedOfferings === 'number' &&
    typeof v.currentPublishedOfferings === 'number'
  );
}

/** Recovers the structured 409 body from a caught publish/unpublish error, if any. */
export function parsePublishBlockReason(error: unknown): OfferingPublishBlockReason | null {
  if (!(error instanceof Error) || !error.message) return null;
  try {
    const parsed: unknown = JSON.parse(error.message);
    return isOfferingPublishBlockReason(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export const OFFERING_TYPE_LABELS: Record<string, string> = {
  ONE_TO_ONE: 'One-to-One',
  GROUP: 'Group',
  CRASH_COURSE: 'Crash Course',
  COURSE: 'Crash Course',
};

export function offeringTypeLabel(type: string): string {
  return OFFERING_TYPE_LABELS[type] ?? type;
}

/** "SILVER" -> "your Silver plan"; "NONE"/null -> "your current plan (no active subscription)". */
export function tierPhrase(tier: string | null | undefined): string {
  if (!tier || tier === 'NONE') return 'your current plan (no active subscription)';
  const cased = tier.charAt(0) + tier.slice(1).toLowerCase();
  return `your ${cased} plan`;
}

/** Human copy for a blocked-publish reason. Pass the offering's own type when
 * known so an OFFERING_TYPE_NOT_ALLOWED message can name it specifically. */
export function describePublishBlockReason(
  reason: OfferingPublishBlockReason,
  attemptedOfferingType?: string,
): { heading: string; body: string } {
  if (reason.code === 'OFFERING_TYPE_NOT_ALLOWED') {
    const allowedList = reason.allowedOfferingTypes.length
      ? reason.allowedOfferingTypes.map(offeringTypeLabel).join(' or ')
      : 'no offering types';
    const subject = attemptedOfferingType
      ? `${offeringTypeLabel(attemptedOfferingType)} offerings aren't`
      : "This offering type isn't";
    return {
      heading: 'This offering type needs a higher plan',
      body: `${subject} included with ${tierPhrase(reason.tier)} — it only allows ${allowedList}. Upgrade your plan to publish it.`,
    };
  }
  const plural = reason.maxPublishedOfferings === 1 ? '' : 's';
  return {
    heading: "You've reached your plan's publish limit",
    body: `${tierPhrase(reason.tier)} allows up to ${reason.maxPublishedOfferings} published offering${plural}, and you currently have ${reason.currentPublishedOfferings} of ${reason.maxPublishedOfferings} published. Unpublish one, or upgrade your plan for more room.`,
  };
}

/**
 * Best-effort, proactive check from `GET /entitlements`, mirroring the 409
 * body's shape so the same copy/banner renders whether the block is known
 * before or only discovered after clicking Publish. Only meaningful for an
 * offering that is NOT currently published (its own publish wouldn't yet be
 * counted in `currentPublishedOfferings`).
 */
export function computeProactivePublishBlock(
  offeringType: string | undefined,
  entitlements: EntitlementsLike | undefined,
): OfferingPublishBlockReason | null {
  if (!offeringType || !entitlements) return null;
  const tier = entitlements.tier ?? 'NONE';
  if (!entitlements.allowedOfferingTypes.includes(offeringType)) {
    return {
      code: 'OFFERING_TYPE_NOT_ALLOWED',
      allowedOfferingTypes: entitlements.allowedOfferingTypes,
      maxPublishedOfferings: entitlements.maxPublishedOfferings,
      currentPublishedOfferings: entitlements.currentPublishedOfferings,
      tier,
    };
  }
  if (entitlements.currentPublishedOfferings >= entitlements.maxPublishedOfferings) {
    return {
      code: 'OFFERING_LIMIT_REACHED',
      allowedOfferingTypes: entitlements.allowedOfferingTypes,
      maxPublishedOfferings: entitlements.maxPublishedOfferings,
      currentPublishedOfferings: entitlements.currentPublishedOfferings,
      tier,
    };
  }
  return null;
}

/**
 * True when a currently-published offering's own type would still be
 * publishable under the teacher's current entitlements. Used to warn before
 * an Unpublish click — once unpublished, republishing is subject to today's
 * (possibly narrower) allowed-types list, not whatever applied when it first
 * went live.
 */
export function isOfferingTypeStillAllowed(
  offeringType: string | undefined,
  entitlements: EntitlementsLike | undefined,
): boolean {
  if (!offeringType || !entitlements) return true;
  return entitlements.allowedOfferingTypes.includes(offeringType);
}
